import React, { useEffect, useState } from "react";
import { useUser } from "../contextPage/UserContext";
import { useCart } from "../contextPage/CartContext";
import { useNavigate } from "react-router-dom";
import {
    API,
    IMAGE_PATH,
    fetchMyOrders,
    fetchOrderDetails,
    createCancelRequest,
    createRefundRequest,
} from "../services/api";
import "./ProfilePage.css";
import Footer from "../components/Footer/Footer";

// Treat ACTIVE as PAID-like in dev/sandbox to allow request testing
const isPaidLike = (status) => {
    const s = String(status || "").toUpperCase();
    const inDev =
        process.env.NODE_ENV !== "production" ||
        process.env.REACT_APP_CF_TEST_MODE === "true";
    return s === "PAID" || (inDev && s === "ACTIVE");
};

function withinRefundWindow(created_at, days = 7) {
    const diffDays = (Date.now() - new Date(created_at).getTime()) / 86400000;
    return diffDays <= days;
}

const ProfilePage = () => {
    const { user, setUser } = useUser();
    const { cart } = useCart();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");
    const [openId, setOpenId] = useState(null);
    const [details, setDetails] = useState({});
    const [busy, setBusy] = useState({});
    const [activeTab, setActiveTab] = useState("orders"); // "cart" | "orders"
    const navigate = useNavigate();

    useEffect(() => {
        if (user) loadOrders();
        else setOrders([]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    async function loadOrders() {
        try {
            setLoading(true);
            setErr("");
            const data = await fetchMyOrders(); // show all statuses
            setOrders(Array.isArray(data) ? data : []);
        } catch (e) {
            setErr(e.message || "Failed to fetch orders");
        } finally {
            setLoading(false);
        }
    }

    async function toggleOrder(order_pk) {
        setOpenId((prev) => (prev === order_pk ? null : order_pk));
        if (!details[order_pk]) {
            try {
                const d = await fetchOrderDetails(order_pk);
                setDetails((prev) => ({ ...prev, [order_pk]: d }));
            } catch (e) {
                console.error("Failed to load order details:", e);
            }
        }
    }

    // -------- Request-based actions (admin + DB) --------
    async function onRequestCancel(order) {
        const reason =
            window.prompt("Why do you want to cancel?", "Changed my mind") || "";
        try {
            setBusy((b) => ({ ...b, [order.order_pk]: "req-cancel" }));
            const resp = await createCancelRequest(order.order_pk, reason);
            alert(`Cancel request submitted. Status: ${resp.status || "PENDING"}`);
        } catch (e) {
            alert(e.message || "Failed to submit cancel request");
        } finally {
            setBusy((b) => ({ ...b, [order.order_pk]: undefined }));
        }
    }

    async function onRequestRefund(order) {
        if (!isPaidLike(order.status)) {
            alert("Only paid orders can be refunded.");
            return;
        }
        if (!withinRefundWindow(order.created_at)) {
            alert("Refund window expired.");
            return;
        }
        const full = Number(order.amount || 0);
        const amountStr = window.prompt("Requested refund amount (₹):", String(full));
        if (amountStr == null) return;
        const amount = Number(amountStr);
        if (!(amount > 0 && amount <= full)) {
            alert("Invalid amount.");
            return;
        }
        const reason = window.prompt("Why refund?", "Did not like the product") || "";

        try {
            setBusy((b) => ({ ...b, [order.order_pk]: "req-refund" }));
            const resp = await createRefundRequest(order.order_pk, amount, reason);
            alert(`Refund request submitted. Status: ${resp.status || "PENDING"}`);
        } catch (e) {
            alert(e.message || "Failed to submit refund request");
        } finally {
            setBusy((b) => ({ ...b, [order.order_pk]: undefined }));
        }
    }

    const handleLogout = async () => {
        try {
            const token = localStorage.getItem("token");
            await fetch(API.logout, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });
        } catch (err) {
            console.error("Logout failed:", err);
        } finally {
            localStorage.removeItem("token");
            setUser(null);
            navigate("/login");
        }
    };

    return (
        <>
            {!user ? (
                <div className="not-logged-in-container" aria-modal="true" role="dialog">
                    <div className="not-logged-in-card">
                        <button
                            type="button"
                            className="not-logged-in-close"
                            aria-label="Close"
                            onClick={() => navigate("/")}
                        >
                            ×
                        </button>
                        <div className="not-logged-in-orbit">
                            <span className="orbit-ring ring-outer" />
                            <span className="orbit-ring ring-inner" />
                            <span className="orbit-dot dot-1" />
                            <span className="orbit-dot dot-2" />
                            <span className="orbit-dot dot-3" />
                            <div className="orbit-center">
                                <span className="orbit-lock-top" />
                                <span className="orbit-lock-body" />
                            </div>
                        </div>

                        <div className="not-logged-in-copy">
                            <h2>Sign in to see your journey</h2>
                            <p className="not-logged-in-subtitle">
                                Track your orders, view your favourites, and enjoy a faster checkout
                                experience with your Swarna Prakash Jewellers account.
                            </p>

                            <ul className="not-logged-in-benefits">
                                <li>See all your past and current orders in one place</li>
                                <li>Reorder your favourites in just a few clicks</li>
                                <li>Save delivery details for quicker checkout</li>
                            </ul>

                            <button
                                onClick={() => navigate("/login")}
                                className="login-btn login-btn-animated"
                                type="button"
                            >
                                Login to your account
                            </button>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="profile-container">
                    <div className="container prof">
                        {/* Header */}
                        <div className="profile-header">
                            <div className="profile-user-info">
                                <div>
                                    <h2>Welcome, {user.name}</h2>
                                    <p>Email: {user.email}</p>
                                </div>
                            </div>
                            <button onClick={handleLogout} className="logout-btn">
                                Logout
                            </button>
                        </div>

                        {/* Tabs */}
                        <div className="tabs">
                            <button
                                className={`tab ${activeTab === "orders" ? "tab-active" : ""}`}
                                onClick={() => setActiveTab("orders")}
                                type="button"
                            >
                                Orders
                            </button>
                            <button
                                className={`tab ${activeTab === "cart" ? "tab-active" : ""}`}
                                onClick={() => setActiveTab("cart")}
                                type="button"
                            >
                                Cart
                            </button>
                        </div>

                        {/* Panels */}
                        {activeTab === "cart" ? (
                            <section className="tab-panel">
                                <h3 className="panel-title">Your Cart</h3>
                                {cart.length === 0 ? (
                                    <p>🛒 Cart is empty.</p>
                                ) : (
                                    <div className="cart-grid">
                                        {cart.map((item) => {
                                            const parsedImages = Array.isArray(item.images)
                                                ? item.images
                                                : JSON.parse(item.images || "[]");
                                            const imageUrl =
                                                parsedImages.length > 0
                                                    ? `${API.imagePath}${parsedImages[0]}`
                                                    : "/placeholder.jpg";
                                            return (
                                                <div
                                                    key={item.id}
                                                    className="cart-card"
                                                    onClick={() => navigate("/cart")}
                                                    style={{ cursor: "pointer" }}
                                                >
                                                    <img
                                                        src={imageUrl}
                                                        alt={item.productName}
                                                        className="cart-card-image"
                                                        onError={(e) =>
                                                            (e.currentTarget.src = "/placeholder.jpg")
                                                        }
                                                    />
                                                    <div className="cart-card-details">
                                                        <h4>{item.productName}</h4>
                                                        <p>Qty: {item.quantity}</p>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </section>
                        ) : (
                            <section className="tab-panel">
                                <h3 className="panel-title">Your Orders</h3>
                                {loading ? (
                                    <p>Loading orders…</p>
                                ) : err ? (
                                    <p style={{ color: "crimson" }}>{err}</p>
                                ) : orders.length === 0 ? (
                                    <p>No orders found.</p>
                                ) : (
                                    <div className="order-list-grid">
                                        {orders.map((o) => {
                                            const placed = o.created_at
                                                ? new Date(o.created_at).toLocaleString()
                                                : "-";
                                            const total = Number(o.amount || 0).toLocaleString(
                                                undefined,
                                                { maximumFractionDigits: 2 }
                                            );

                                            return (
                                                <div key={o.order_pk} className="order-card">
                                                    <div className="order-card-top">
                                                        <div>
                                                            <div className="order-id">
                                                                Order #{o.local_order_id}
                                                            </div>
                                                            <div className="order-meta">Placed: {placed}</div>
                                                            <div
                                                                className={`order-status pill ${String(
                                                                    o.status || ""
                                                                ).toLowerCase()}`}
                                                            >
                                                                {o.status}
                                                            </div>
                                                        </div>

                                                        <div className="order-actions">
                                                            <div className="order-amount">
                                                                ₹{total} {o.currency ? `(${o.currency})` : ""}
                                                            </div>

                                                            <button
                                                                className="btn-outline"
                                                                onClick={() => toggleOrder(o.order_pk)}
                                                            >
                                                                {openId === o.order_pk
                                                                    ? "Hide details"
                                                                    : "View details"}
                                                            </button>

                                                            {(o.status === "CREATED" || isPaidLike(o.status)) && (
                                                                <button
                                                                    className="btn-outline"
                                                                    onClick={() => onRequestCancel(o)}
                                                                    disabled={busy[o.order_pk] === "req-cancel"}
                                                                >
                                                                    {busy[o.order_pk] === "req-cancel"
                                                                        ? "Submitting…"
                                                                        : "Request cancel"}
                                                                </button>
                                                            )}

                                                            {isPaidLike(o.status) &&
                                                                withinRefundWindow(o.created_at) && (
                                                                    <button
                                                                        className="btn-outline"
                                                                        onClick={() => onRequestRefund(o)}
                                                                        disabled={busy[o.order_pk] === "req-refund"}
                                                                    >
                                                                        {busy[o.order_pk] === "req-refund"
                                                                            ? "Submitting…"
                                                                            : "Request refund"}
                                                                    </button>
                                                                )}
                                                        </div>
                                                    </div>

                                                    {/* One product per line */}
                                                    {!!o.items?.length && (
                                                        <div className="order-thumbs">
                                                            {o.items.map((it, idx) => {
                                                                let first = "/fallback.avif";
                                                                try {
                                                                    const arr = Array.isArray(it.images)
                                                                        ? it.images
                                                                        : JSON.parse(it.images || "[]");
                                                                    if (arr?.length) first = IMAGE_PATH + arr[0];
                                                                } catch { }
                                                                const unit = Number(it.unit_price || 0);
                                                                const qty = Number(it.quantity || 0);
                                                                const subtotal = (unit * qty).toLocaleString();

                                                                return (
                                                                    <div
                                                                        key={`${it.product_id}-${idx}`}
                                                                        className="thumb-row"
                                                                    >
                                                                        <img
                                                                            src={first}
                                                                            alt={it.productName}
                                                                            className="thumb-img"
                                                                            onError={(e) =>
                                                                                (e.currentTarget.src = "/fallback.avif")
                                                                            }
                                                                        />
                                                                        <div className="thumb-info">
                                                                            <div className="thumb-name">
                                                                                {it.productName}
                                                                            </div>
                                                                            <div className="thumb-meta">
                                                                                x{qty} • ₹{unit.toLocaleString()}
                                                                                {it.size && (
                                                                                    <span className="product-size"> • Size: {it.size}</span>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                        <div className="thumb-right">
                                                                            ₹{subtotal}
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    )}

                                                    {/* {openId === o.order_pk && details[o.order_pk]?.order && (
                                                        <div className="order-details">
                                                            <h4>Payment</h4>
                                                            <div className="payment-grid">
                                                                <div>
                                                                    <b>Status:</b>{" "}
                                                                    {details[o.order_pk].order.status}
                                                                </div>
                                                                <div>
                                                                    <b>Method:</b>{" "}
                                                                    {details[o.order_pk].order.payment_method ||
                                                                        "N/A"}
                                                                </div>
                                                                <div>
                                                                    <b>CF Payment ID:</b>{" "}
                                                                    {details[o.order_pk].order.cf_payment_id ||
                                                                        "—"}
                                                                </div>
                                                                <div>
                                                                    <b>Email:</b>{" "}
                                                                    {details[o.order_pk].order.customer_email}
                                                                </div>
                                                                <div>
                                                                    <b>Phone:</b>{" "}
                                                                    {details[o.order_pk].order.customer_phone}
                                                                </div>
                                                                <div>
                                                                    <b>Updated:</b>{" "}
                                                                    {new Date(
                                                                        details[o.order_pk].order.updated_at
                                                                    ).toLocaleString()}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )} */}

                                                    {openId === o.order_pk && (
                                                        <div className="order-details-modern">

                                                            <div className="details-grid">

                                                                {/* SHIPPING */}
                                                                <div className="details-box">
                                                                    <h5>Delivery Address</h5>

                                                                    {o.shipping?.name ? (
                                                                        <>
                                                                            <p className="detail-strong">{o.shipping.name}</p>
                                                                            {o.shipping.line1 && <p>{o.shipping.line1}</p>}
                                                                            {o.shipping.line2 && <p>{o.shipping.line2}</p>}

                                                                            {(o.shipping.city || o.shipping.state || o.shipping.pincode) && (
                                                                                <p>
                                                                                    {o.shipping.city}, {o.shipping.state} {o.shipping.pincode}
                                                                                </p>
                                                                            )}

                                                                            {o.shipping.country && <p>{o.shipping.country}</p>}
                                                                            {o.shipping.phone && <p>📞 {o.shipping.phone}</p>}
                                                                        </>
                                                                    ) : (
                                                                        <p className="no-shipping">
                                                                            Address not available
                                                                        </p>
                                                                    )}
                                                                </div>

                                                                {/* PAYMENT */}
                                                                <div className="details-box">
                                                                    <h5>Payment Details</h5>

                                                                    <p><span>Status:</span> {o.status}</p>
                                                                    <p><span>Total:</span> ₹{Number(o.amount).toLocaleString()}</p>
                                                                    <p><span>Order ID:</span> {o.local_order_id}</p>
                                                                    <p>
                                                                        <span>Placed:</span>{" "}
                                                                        {o.created_at
                                                                            ? new Date(o.created_at).toLocaleString()
                                                                            : "-"}
                                                                    </p>
                                                                </div>

                                                            </div>

                                                        </div>
                                                    )}


                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </section>
                        )}
                    </div>
                </div>
            )}
            <Footer />
        </>
    );
};

export default ProfilePage;
